import { FC, createRef, useEffect, useMemo } from 'react';
import { generateSequentialNumbers } from '../../../../../services/generateSequentialNumbers';
import styles from './styles.module.css';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute } from '@fortawesome/free-solid-svg-icons';
import TimeCellData from '../TimeCellData';
import { parseTime } from '../../../../../services/parseTime';
import { Line, generateAllocationTask } from '../allocationTableUtility';
import { useDispatch, useSelector } from 'react-redux';
import { setEditTask } from '../../../../../redux/carAllocationEditSlice';
import { AppDispatch, AppState } from '../../../../../redux/store';
import { getWorkCategoryIDByName } from '../../../../../services/workCategory';
import { format } from 'date-fns';

type Props = {
  firstDate: boolean;
  line: Line;
  originDate: Date;
  onClickTime?: (elem: HTMLElement, time: Date) => void;
};
const times = generateSequentialNumbers(0, 23).flatMap((time) => [
  `${time}:00`,
  `${time}:30`,
]);
const AllocationTableRow: FC<Props> = ({
  firstDate,
  line,
  originDate,
  onClickTime,
}) => {
  const { editMode, allocationTasks } = useSelector(
    (state: AppState) => state.carAllocationEdit
  );
  const allocationsFromTask = useMemo(() => {
    return allocationTasks.filter(
      (t) => t.userId === line.userId && t.carId === line.carId
    );
  }, [allocationTasks]);
  const allocationsFromLine = useMemo(() => {
    return generateAllocationTask(line.allocations, line.userId, line.carId);
  }, [line.allocations]);
  const allocations = editMode ? allocationsFromTask : allocationsFromLine;

  const workStartTime = useMemo(() => {
    if (allocations.length === 0) {
      return undefined;
    }

    const departureAllocation = allocations.find(
      (a) => a.workCategory.id === getWorkCategoryIDByName('出発')
    );
    if (!departureAllocation) {
      return parseTime('00:00', originDate);
    }

    return new Date(departureAllocation.startDateForPlacement);
  }, [allocations]);
  const workEndTime = useMemo(() => {
    if (allocations.length === 0) {
      return undefined;
    }

    const arrivalAllocation = allocations.find(
      (a) => a.workCategory.id === getWorkCategoryIDByName('到着')
    );
    if (!arrivalAllocation) {
      return parseTime('24:00', originDate);
    }

    return new Date(arrivalAllocation.endDateForPlacement);
  }, [allocations]);
  const dispatch = useDispatch<AppDispatch>();

  const operatingRouteLinkQueryParams = new URLSearchParams({
    date: format(originDate, 'yyyy-MM-dd'),
    driverId: line.userId ? `${line.userId}` : '',
  }).toString();

  const scrollRef = createRef<HTMLDivElement>();
  useEffect(() => {
    // 初期表示の場所を設定
    scrollRef?.current?.scrollIntoView({
      inline: 'start',
      block: 'end',
      behavior: 'auto',
    });
  }, []);

  return (
    <div className={styles.lineContainer}>
      <div className={styles.timelineGroupKeyCell}>
        <div className={styles.groupKeyTextContainer}>
          <div className={styles.title}>{line.title}</div>
          {line.sub && <div className={styles.sub}>{line.sub}</div>}
        </div>
        {line.userId && (
          <Link
            to={`/operating-route?${operatingRouteLinkQueryParams}`}
            className={styles.toRoute}
          >
            <FontAwesomeIcon icon={faRoute} className={styles.toRouteIcon} />
            ルート
          </Link>
        )}
      </div>
      {times.map((time) => {
        // refにてドライバー名の下にも時間が2.5h分存在するため、表示したい時間-2.5hの時間である4:00を指定する
        return (
          <div
            key={time}
            className={styles.cell}
            ref={time === '4:00' && firstDate ? scrollRef : undefined}
          >
            <TimeCellData
              workStartTime={workStartTime}
              workEndTime={workEndTime}
              restStartTime={
                line.restTime?.start
                  ? parseTime(line.restTime.start, originDate)
                  : undefined
              }
              restEndTime={
                line.restTime?.end
                  ? parseTime(line.restTime.end, originDate)
                  : undefined
              }
              currentTime={parseTime(time, originDate)}
              carId={line.carId}
              userId={line.userId}
              allocations={allocations}
              isAvailable={line.isAvailable}
              onClick={(elem) =>
                onClickTime?.(elem, parseTime(time, originDate))
              }
              onClickAddButton={() => {
                dispatch(
                  setEditTask({
                    type: 'unallocated',
                    primary: {
                      type: 'unallocated',
                      startDate: parseTime(time, originDate).toISOString(),
                      carId: line.carId,
                      userId: line.userId,
                    },
                    secondary: {
                      type: 'unallocated',
                      carId: line.carId,
                      userId: line.userId,
                    },
                    targetCarId: line.carId,
                    targetUserId: line.userId,
                    step: 1,
                    ...(line.order === 'driver'
                      ? {
                          selectedCarName: line.sub,
                        }
                      : {
                          selectedUserName: line.sub,
                        }),
                  })
                );
              }}
              allowedDragAndDrop={editMode}
            />
          </div>
        );
      })}
    </div>
  );
};

export default AllocationTableRow;
